import { forwardRef, useCallback, useEffect } from 'react';

import type { SvpCategoryAdditional } from '@vgtv/api-client/lib/svp_category';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type {
  AssetCarouselComponent,
  CategoryCarouselComponent,
  MixedCarouselComponent,
} from '@vgtv/api-client/lib/component/carousel';

import { isSvpCategory } from '../../modules/helpers';
import { getCategoryPromoUrl } from '../../shared/helpers/categoryHelpers';
import { getAssetPromoUrl } from '../../shared/helpers/assetHelpers';
import { useServerTime } from '../utils/useServerTime';
import { useViewportType } from '../utils/useViewportType';
import { useVendor } from '../VendorPovider/VendorPovider';
import { useNetworkInformation } from '../utils/useNetworkInformation';
import { useSplashTracking } from '../PageComponents/common/useSplashTracking';
import { useComponentItems } from '../utils/useComponentItems';

import { useCarousel } from './DesktopCarousel/Carousel/useCarousel';
import { DesktopCarousel } from './DesktopCarousel/DesktopCarousel';
import { MobileCarousel } from './MobileCarousel/MobileCarousel';
import { withCarouselTracking } from './withCarouselTracking';

const getPreviewUrl = (item: SvpAssetAdditional | SvpCategoryAdditional) => {
  return isSvpCategory(item)
    ? getCategoryPromoUrl(item)
    : getAssetPromoUrl(item);
};

interface CarouselProps {
  isFirstComponent: boolean;
  pageComponentIndex: number;
  componentConfig:
    | MixedCarouselComponent
    | AssetCarouselComponent
    | CategoryCarouselComponent;
}

const CAROUSEL_INTERVAL_IN_MILLISECONDS = 6000;

const CarouselBase = forwardRef<HTMLDivElement, CarouselProps>(
  ({ componentConfig, isFirstComponent, pageComponentIndex }, ref) => {
    const { serverTime } = useServerTime();
    const { isMobile, isDesktop } = useViewportType();
    const { shouldSaveData } = useNetworkInformation();
    const { vendor } = useVendor();

    const items = useComponentItems(componentConfig);

    const { currentIndex, setCurrentIndex, goForward } = useCarousel(
      items.length
    );

    const currentItem = items ? items[currentIndex] : undefined;

    const { trackButtonSplashClick } = useSplashTracking({
      elementType: 'MainCarousel',
    });

    // Cycle through the carousel items when there is no preview
    useEffect(() => {
      if (!currentItem) {
        return;
      }

      const previewUrl = getPreviewUrl(currentItem);

      if (previewUrl && !shouldSaveData) {
        return;
      }

      const timeout = window.setTimeout(() => {
        if (!isMobile) {
          goForward();
        }
      }, CAROUSEL_INTERVAL_IN_MILLISECONDS);

      return () => window.clearTimeout(timeout);
    }, [currentItem, shouldSaveData, goForward]);

    const onThumbnailInteraction = useCallback(
      (index?: number) => {
        setCurrentIndex(index ? index : 0);
      },
      [setCurrentIndex]
    );

    if (items.length === 0) {
      return null;
    }

    return (
      <>
        {!isDesktop && currentItem ? (
          <MobileCarousel
            items={items}
            currentItem={currentItem}
            onThumbnailClick={onThumbnailInteraction}
            ref={ref}
            vendor={vendor}
          ></MobileCarousel>
        ) : (
          <DesktopCarousel
            ref={ref}
            currentIndex={currentIndex}
            onCurrentIndexChange={setCurrentIndex}
            isFirstComponent={isFirstComponent}
            items={items}
            vendor={vendor}
            serverTime={serverTime}
            trackButtonSplashClick={trackButtonSplashClick}
            pageComponentIndex={pageComponentIndex}
            onThumbnailHover={onThumbnailInteraction}
          ></DesktopCarousel>
        )}
      </>
    );
  }
);

export const Carousel = withCarouselTracking(CarouselBase);
